import { Footer } from '@components/footer';
import { SvgSources } from '@components/icons/svgSources';
import { Navbar } from '@components/navbar';
import { Toaster } from '@components/ui/toast/toaster';
import { UploadQueue } from '@components/uploadQueue';
import { useAppContext } from '@contexts/app';
import { DropdownsProvider } from '@contexts/dropdowns';
import { useWebsocketContext } from '@contexts/websocket';
import { useRevalidateQuery } from '@services/revalidateQueries';
import { cn } from '@utils/cn';
import { appRoutes, foremanRoute } from '@utils/consts';
import { handleLanguageChange } from '@utils/i18n';
import { formatResponse } from '@utils/textHelpers';
import { useEffect, useMemo } from 'react';
import { Location, Outlet, useLocation, useParams } from 'react-router';
import { Channel } from '@anycable/web';
import { AppScrollRestoration } from '@components/scrollRestoration';
import { APP_SCROLL_CLASS, APP_SUBHEADER_SLOT_ID } from '@hooks/appScroll/constants';

const getScrollKey = (location: Location) =>
  location.pathname.includes(foremanRoute.index) ? location.pathname : location.key;

type Message = {
  actions?: string[];
  hotSeatAlerts?: boolean;
  onDeckAlerts?: boolean;
};

export const Layout = () => {
  const { isLogged: isUserLogged, admin, id, pushEnabled, tenant } = useAppContext();
  const { revalidate } = useRevalidateQuery();
  const { pathname } = useLocation();
  const { lang } = useParams();
  const { ws } = useWebsocketContext();
  const isLogged = isUserLogged || admin?.isLogged;

  const notFound = pathname.includes(appRoutes.notFound);

  const userTenantId = 'id' in tenant ? tenant.id : undefined;

  useEffect(() => {
    if (!id || !ws || !userTenantId) return;
    if (!isUserLogged) return;

    const channel = new Channel({ channel: 'RefreshChannel', tenant: userTenantId });
    ws.subscribe(channel);

    channel.on('message', (msg) => {
      const transformedMsg = formatResponse(msg as Message);
      if (transformedMsg.actions) {
        revalidate(transformedMsg.actions).catch((err) => {
          console.error(err);
        });
      }
    });

    return () => {
      channel.disconnect();
    };
  }, [id, ws, userTenantId, revalidate, isUserLogged]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    handleLanguageChange(lang || 'en');
  }, [lang]);

  const outletKey = useMemo(() => (pushEnabled ? new Date().toISOString() : ''), [pushEnabled]);

  return (
    <DropdownsProvider>
      <div
        className={cn(
          'flex min-h-screen w-full flex-col md:h-dv md:min-h-0 md:overflow-hidden',
          isLogged && !notFound ? '' : 'login-page',
        )}>
        <SvgSources />
        <Navbar />
        <div id={APP_SUBHEADER_SLOT_ID} className="contents" />
        {/* <Tutorial /> */}
        <div
          className={cn(
            APP_SCROLL_CLASS,
            'scrollbar-md flex grow flex-col md:min-h-0 md:overflow-y-auto md:[scrollbar-gutter:stable]',
          )}>
          <main
            key={outletKey}
            className={cn(
              'main mx-auto w-full pb-5 p-5',
              isLogged && !notFound ? 'grow' : 'pt-10',
            )}>
            <Outlet />
          </main>
          <Footer isCompact={!isLogged || notFound} />
        </div>
        <AppScrollRestoration getKey={getScrollKey} />
        <Toaster />
        <UploadQueue />
      </div>
    </DropdownsProvider>
  );
};
